import { initializeApp, type FirebaseApp } from 'firebase/app';
import { initializeAppCheck, ReCaptchaEnterpriseProvider } from 'firebase/app-check';
import { getAnalytics, isSupported as analyticsSupported } from 'firebase/analytics';
import { connectAuthEmulator, getAuth } from 'firebase/auth';
import { connectFirestoreEmulator, getFirestore } from 'firebase/firestore';
import { connectFunctionsEmulator, getFunctions } from 'firebase/functions';
import { connectStorageEmulator, getStorage } from 'firebase/storage';
import { isNative } from './platform';

const useEmulators = import.meta.env.VITE_USE_EMULATORS === 'true';

// Firebase web config is public, but we keep it out of the WEB repo/bundle by
// loading the per-site config Firebase Hosting serves at /__/firebase/init.json
// (same-origin on the deployed site; local dev proxies it to the dev site, see
// vite.config.ts). The native app loads from capacitor://localhost (not Hosting,
// and a cross-origin fetch of init.json would hit CORS), so it bakes the config
// at build time from env instead. Top-level await so importers get a ready app.
async function loadConfig(): Promise<Record<string, string | undefined>> {
  if (!isNative && !useEmulators) {
    try {
      const response = await fetch('/__/firebase/init.json');
      if (response.ok) return await response.json();
    } catch {
      /* fall through to env (local builds without Hosting) */
    }
  }
  return {
    apiKey: import.meta.env.VITE_FIREBASE_API_KEY,
    authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN,
    projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID,
    storageBucket: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET,
    messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID,
    appId: import.meta.env.VITE_FIREBASE_APP_ID,
    measurementId: import.meta.env.VITE_FIREBASE_MEASUREMENT_ID,
  };
}

const config = await loadConfig();

// A native build with no baked config would silently init a broken app; fail loud.
if (isNative && (!config.apiKey || !config.projectId)) {
  throw new Error('Native Firebase config missing. Set VITE_FIREBASE_* for the native build.');
}

export const firebaseApp: FirebaseApp = initializeApp(config);

// App Check (web only) attests requests via reCAPTCHA Enterprise. Enforcement is
// applied only to public marketing forms server-side, so a token hiccup can't
// lock users out of the rest of the app. Native uses no App Check provider here.
const recaptchaSiteKey = import.meta.env.VITE_RECAPTCHA_SITE_KEY;
if (!isNative && !useEmulators && recaptchaSiteKey) {
  initializeAppCheck(firebaseApp, {
    provider: new ReCaptchaEnterpriseProvider(recaptchaSiteKey),
    isTokenAutoRefreshEnabled: true,
  });
}

export const auth = getAuth(firebaseApp);
export const db = getFirestore(firebaseApp);
export const storage = getStorage(firebaseApp);
export const functions = getFunctions(firebaseApp);

if (useEmulators) {
  connectAuthEmulator(auth, 'http://localhost:9099', { disableWarnings: true });
  connectFirestoreEmulator(db, 'localhost', 8080);
  connectStorageEmulator(storage, 'localhost', 9199);
  connectFunctionsEmulator(functions, 'localhost', 5001);
} else if (config.measurementId) {
  analyticsSupported()
    .then((supported) => {
      if (supported) getAnalytics(firebaseApp);
    })
    .catch(() => undefined);
}
